import { throttle } from 'lodash';
import React, { Component } from 'react';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom-v5-compat';

import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { UploadStatusType } from '~/enums/UploadStatusType';
import { FormattedMessage, injectIntl } from 'react-intl';
import { slices } from '~/redux/slices';
import { getUserUploads } from '~/redux/thunks/fileUpload';
import { withRouting } from '~/routing/withRouting';

import {
  black2,
  evisortBlue,
  evisortOrange,
  evisortRed,
  evisortYellow,
} from '../../../assets/shared-styles/general';
import { featureFlagIncluded } from '../../../utils/user';
import { documentTypeIcon } from '../../DocumentsPage/Document.utils';
import CheckmarkIcon from '../../Shared/Icons/CheckmarkIcon';
import CloseIcon from '../../Shared/Icons/CloseIcon';
import QueueIcon from '../../Shared/Icons/QueueIcon';
import SyncIcon from '../../Shared/Icons/SyncIcon';
import UploadArrowUpIcon from '../../Shared/Icons/UploadArrowUpIcon';
import WarningIcon from '../../Shared/Icons/WarningIcon';
import EcCard from '../EcCard/EcCard';
import styles from './UploadInfoPanel.styles';

const REFRESH_LOG_INTERVAL_MS = 5000;

class UploadInfoPanel extends Component {
  constructor(props) {
    super(props);

    this.loadUploadLogs = throttle(
      this.loadUploadLogs,
      REFRESH_LOG_INTERVAL_MS,
    );
  }

  componentDidMount() {
    this.props.onRef(this);
    this.loadUploadLogs();
    this.interval = setInterval(() => this.refreshUploadLogs(), 1000);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  loadUploadLogs() {
    this.props.getUserUploads();
  }

  refreshUploadLogs() {
    const { open, uploadLogs } = this.props;
    const anyProcessingFile =
      uploadLogs &&
      uploadLogs.some(
        (log) =>
          log.processing_status === UploadStatusType.Uploading ||
          log.processing_status === UploadStatusType.Queued ||
          log.processing_status === UploadStatusType.Processing,
      );

    if (open && anyProcessingFile) {
      this.loadUploadLogs();
    }
  }

  getUploadStatusTooltip(status) {
    const { intl } = this.props;

    switch (status) {
      case 'COMPLETED':
        return intl.formatMessage({
          id: 'UploadInfoPanel.completedTooltip',
          defaultMessage:
            'Your document was uploaded and processed successfully',
          description: 'Tooltip for completed upload status',
        });
      case 'QUEUED':
        return intl.formatMessage({
          id: 'UploadInfoPanel.queuedTooltip',
          defaultMessage:
            'Your document was uploaded successfully and has been placed in line for processing, which may take several minutes',
          description: 'Tooltip for queued upload status',
        });
      case 'PROCESSING':
        return intl.formatMessage({
          id: 'UploadInfoPanel.processingTooltip',
          defaultMessage:
            'Your document is being processed and should be available shortly',
          description: 'Tooltip for processing upload status',
        });
      case 'UPLOAD_FAILED':
        return intl.formatMessage({
          id: 'UploadInfoPanel.uploadFailedTooltip',
          defaultMessage:
            'Your document was not processed due to a system error. Please try uploading again later or contact support.',
          description: 'Tooltip for upload failed status',
        });
      case 'UPLOAD_FILETYPE_FAILURE':
        return intl.formatMessage({
          id: 'UploadInfoPanel.uploadFiletypeFailureTooltip',
          defaultMessage:
            'Your document was not uploaded because its file type is not supported.',
          description: 'Tooltip for unsupported file type status',
        });
      case 'UPLOADING':
        return intl.formatMessage({
          id: 'UploadInfoPanel.uploadingTooltip',
          defaultMessage: 'Your document is uploading',
          description: 'Tooltip for uploading status',
        });
      case 'OCR_FAILURE':
        return intl.formatMessage({
          id: 'UploadInfoPanel.ocrFailureTooltip',
          defaultMessage:
            'Your document was not processed due to a system error. Please try uploading again later or contact support.',
          description: 'Tooltip for OCR failure status',
        });
      case 'ALGORITHM_FAILURE':
        return intl.formatMessage({
          id: 'UploadInfoPanel.algorithmFailureTooltip',
          defaultMessage:
            'Your document was not processed due to a system error. Please try uploading again later or contact support.',
          description: 'Tooltip for algorithm failure status',
        });
      default:
        return undefined;
    }
  }

  renderStatusIcon(status) {
    const { user } = this.props;

    switch (status) {
      case UploadStatusType.Completed:
        return <CheckmarkIcon size="20" green />;
      case UploadStatusType.Queued:
        return <QueueIcon color={black2} />;
      case UploadStatusType.Processing:
        return <SyncIcon size="20" color={evisortBlue} />;
      case UploadStatusType.ProcessingError:
      case UploadStatusType.UploadFailed:
        return <CloseIcon size="20" color={evisortRed} />;
      case UploadStatusType.Uploading:
        return <UploadArrowUpIcon size="20" color={evisortYellow} />;
      case UploadStatusType.Duplicate:
        return featureFlagIncluded(user, 'DUPLICATE_STATUS') ? (
          <WarningIcon size="20" color={evisortOrange} />
        ) : (
          <CheckmarkIcon size="20" green />
        );
      case UploadStatusType.OcrFailure:
      case UploadStatusType.AlgorithmFailure:
        return <WarningIcon size="20" yellow />;
      default:
        break;
    }
  }

  renderLogs() {
    const { classes } = this.props;
    const { uploadLogs } = this.props;
    const content = uploadLogs.length
      ? uploadLogs.map((log) => {
          return (
            <div className={classes.itemRow} key={log.id}>
              <span className={classes.documentTypeIcon}>
                {documentTypeIcon(log.file_type)}
              </span>
              <span className={classes.documentName}>{log.document_name}</span>
              <span
                className={classes.docUploadStatus}
                data-tip
                data-for={`uploadStatusTooltip-${log.id}`}
              >
                <Tooltip
                  tooltip={
                    log.processing_status !== 'DUPLICATE'
                      ? this.getUploadStatusTooltip(log.processing_status)
                      : undefined
                  }
                  id={`uploadStatusTooltip-${log.id}`}
                  width="224px"
                  placement="right"
                >
                  {this.renderStatusIcon(log.processing_status)}
                </Tooltip>
              </span>
              <Link
                id={`upload_panel_viewLink?document=${log.id}`}
                to={`/document/${log.id}`}
              >
                <FormattedMessage
                  id="UploadInfoPanel.viewLink"
                  defaultMessage="View"
                  description="Link text to view uploaded document"
                />
              </Link>
            </div>
          );
        })
      : null;
    return <div className={classes.itemContainer}>{content}</div>;
  }

  render() {
    const {
      classes,
      location: { pathname },
    } = this.props;
    const { open } = this.props;

    if (pathname === '/upload' || !open) return null;
    return (
      <div className={classes.uploadInfoPopup}>
        <EcCard>
          <div className={classes.uploadInfoContainer}>
            <FormattedMessage
              id="UploadInfoPanel.uploadLogTitle"
              defaultMessage="Upload Log"
              description="Title for the upload log panel"
            />
            <button
              className={classes.closeIcon}
              onClick={() => this.props.closeUploadInfoPanel()}
            >
              <CloseIcon size="19" />
            </button>
          </div>
          {this.renderLogs()}
        </EcCard>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  open: slices.deprecatedUpload.selectors.selectIsUploadInfoPanelOpen(state),
  uploadLogs: slices.deprecatedUpload.selectors.selectUploadLogs(state),
});
const mapDispatchToProps = {
  getUserUploads,
  closeUploadInfoPanel: slices.deprecatedUpload.actions.closeUploadInfoPanel,
};

UploadInfoPanel = injectIntl(UploadInfoPanel);

UploadInfoPanel = connect(mapStateToProps, mapDispatchToProps)(UploadInfoPanel);

UploadInfoPanel = withRouting(UploadInfoPanel);

export default injectSheet(styles)(UploadInfoPanel);
